import type { TFunction } from 'i18next';
import { cn } from '@/lib/utils';

export interface LearnerEnrollment {
  flowId: string;
  flowTitle: string;
  totalBlocks?: number;
  progress: Record<string, unknown>;
  completed: boolean;
  totalTimeSpent: number;
  startedAt: string | null;
  completedAt: string | null;
  lastAccessAt: string | null;
  tracking?: {
    events?: Array<{
      id?: string;
      type?: string;
      at?: string;
      blockId?: string | null;
      status?: string | null;
      metadata?: Record<string, unknown>;
    }>;
    responses?: Record<string, unknown>;
    blockSessions?: Array<{
      id: string;
      blockId: string;
      blockTitle?: string | null;
      blockType?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
      score?: number | null;
      sessionData?: Record<string, unknown>;
    }>;
  };
}

export interface LearnerEntry {
  participant: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    facePhotoUrl: string | null;
  };
  enrollments: LearnerEnrollment[];
}

export function localeFromLang(code: string | undefined): string {
  const c = (code ?? 'en').split('-')[0].toLowerCase();
  const map: Record<string, string> = {
    fr: 'fr-FR',
    en: 'en-GB',
    de: 'de-DE',
    es: 'es-ES',
    it: 'it-IT',
    pt: 'pt-PT',
    lb: 'lb-LU',
    uk: 'uk-UA',
    ru: 'ru-RU',
  };
  return map[c] ?? 'en-GB';
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h${rm.toString().padStart(2, '0')}` : `${h}h`;
}

export function formatDate(iso: string | null, lang?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(localeFromLang(lang), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(iso: string | null, lang?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(localeFromLang(lang), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type BlockSessionLite = NonNullable<LearnerEnrollment['tracking']>['blockSessions'] extends (infer S)[] | undefined
  ? S
  : never;

export function sessionDurationSeconds(session: BlockSessionLite): number {
  if (!session.startedAt) return 0;
  const t0 = new Date(session.startedAt).getTime();
  if (session.completedAt) {
    const t1 = new Date(session.completedAt).getTime();
    return Math.max(0, Math.round((t1 - t0) / 1000));
  }
  return Math.max(0, Math.round((Date.now() - t0) / 1000));
}

export function getBlockDisplayLabel(blockId: string | null | undefined, tracking: LearnerEnrollment['tracking'] | undefined): string {
  if (!blockId) return '';
  const list = tracking?.blockSessions ?? [];
  const match = list.find((s) => s.blockId === blockId);
  const title = match?.blockTitle?.trim();
  if (title) return title;
  return `Bloc ${blockId.slice(0, 8)}…`;
}

export function aggregateTimeByBlock(sessions: BlockSessionLite[] | undefined): Array<{
  blockId: string;
  title: string;
  seconds: number;
  hasOpenSession: boolean;
}> {
  if (!sessions?.length) return [];
  const map = new Map<string, { title: string; seconds: number; hasOpenSession: boolean }>();
  for (const s of sessions) {
    const sec = sessionDurationSeconds(s);
    const titleFromSession = s.blockTitle?.trim();
    const prev = map.get(s.blockId);
    const title = titleFromSession || prev?.title || `Bloc ${s.blockId.slice(0, 8)}…`;
    const open = !s.completedAt;
    map.set(s.blockId, {
      title,
      seconds: (prev?.seconds ?? 0) + sec,
      hasOpenSession: (prev?.hasOpenSession ?? false) || open,
    });
  }
  return [...map.entries()]
    .map(([blockId, v]) => ({ blockId, ...v }))
    .sort((a, b) => b.seconds - a.seconds);
}

export function extractAdaptiveStats(
  tracking: LearnerEnrollment['tracking'] | undefined,
): {
  evaluated: number;
  replaced: number;
  accepted: number;
  declined: number;
} {
  const events = Array.isArray(tracking?.events) ? tracking?.events ?? [] : [];
  let evaluated = 0;
  let replaced = 0;
  let accepted = 0;
  let declined = 0;
  for (const evt of events) {
    const meta = evt?.metadata && typeof evt.metadata === 'object'
      ? (evt.metadata as Record<string, unknown>)
      : null;
    const adaptive = meta?.adaptiveReplacement && typeof meta.adaptiveReplacement === 'object'
      ? (meta.adaptiveReplacement as Record<string, unknown>)
      : null;
    if (!adaptive) continue;
    const decision = typeof adaptive.decision === 'string' ? adaptive.decision : '';
    const didReplace = adaptive.replaced === true;
    if (decision === 'evaluated') evaluated += 1;
    if (decision === 'assist_accepted') accepted += 1;
    if (decision === 'assist_declined') declined += 1;
    if (didReplace) replaced += 1;
  }
  return { evaluated, replaced, accepted, declined };
}

export function getProgressPercent(
  progress: Record<string, unknown>,
  opts: {
    completed: boolean;
    totalBlocks?: number;
    distinctBlocksFromSessions?: number;
  },
): number {
  if (opts.completed) return 100;

  const total =
    typeof opts.totalBlocks === 'number' && opts.totalBlocks > 0 ? opts.totalBlocks : 0;

  if (total > 0) {
    const cb = progress.completedBlockIds;
    if (Array.isArray(cb) && cb.length > 0) {
      return Math.min(100, Math.round((cb.length / total) * 100));
    }

    const vb = progress.visitedBlockIds;
    if (Array.isArray(vb) && vb.length > 0) {
      return Math.min(100, Math.round((vb.length / total) * 100));
    }

    const blocks = progress.blocks;
    if (blocks && typeof blocks === 'object' && !Array.isArray(blocks)) {
      const vals = Object.values(blocks as Record<string, unknown>);
      const done = vals.filter((v: any) => v?.status === 'completed').length;
      if (done > 0) return Math.min(100, Math.round((done / total) * 100));
      const touched = vals.filter((v: any) =>
        v && typeof v === 'object' && (v.status === 'in_progress' || v.status === 'started' || v.timeSpent != null),
      ).length;
      if (touched > 0) {
        return Math.min(95, Math.max(1, Math.round((touched / total) * 100)));
      }
    }

    const sess = opts.distinctBlocksFromSessions ?? 0;
    if (sess > 0) {
      return Math.min(95, Math.max(1, Math.round((sess / total) * 100)));
    }

    return 0;
  }

  const blocks = progress.blocks;
  if (blocks && typeof blocks === 'object' && !Array.isArray(blocks)) {
    const vals = Object.values(blocks as Record<string, unknown>);
    if (vals.length === 0) return 0;
    const completedN = vals.filter((v: any) => v?.status === 'completed' || v === 'completed').length;
    return Math.round((completedN / vals.length) * 100);
  }

  const cbOnly = progress.completedBlockIds;
  if (Array.isArray(cbOnly) && cbOnly.length > 0) {
    return Math.min(100, cbOnly.length * 25);
  }

  return 0;
}

export function getEnrollmentProgressPercent(enroll: LearnerEnrollment): number {
  const progress =
    enroll.progress && typeof enroll.progress === 'object' && !Array.isArray(enroll.progress)
      ? (enroll.progress as Record<string, unknown>)
      : {};
  const sessions = enroll.tracking?.blockSessions ?? [];
  const distinctBlocksFromSessions = new Set(sessions.map((s) => s.blockId)).size;
  return getProgressPercent(progress, {
    completed: enroll.completed,
    totalBlocks: enroll.totalBlocks,
    distinctBlocksFromSessions,
  });
}

export function formatTrackingEventLabel(type: string | undefined, t: TFunction): string {
  const raw = type?.trim();
  if (!raw) return t('pages.learners.trackingEvent.unknown');
  return t(`pages.learners.trackingEvent.${raw}`, {
    defaultValue: raw.replace(/_/g, ' '),
  });
}

export function getInitials(first: string | null, last: string | null, email: string): string {
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

export function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            percent >= 100 ? 'bg-green-500' : percent > 50 ? 'bg-blue-500' : percent > 0 ? 'bg-amber-500' : 'bg-gray-300',
          )}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      <span className={cn(
        'text-xs font-medium tabular-nums',
        percent >= 100 ? 'text-green-600' : 'text-[#5B6472] dark:text-[#B6CAEC]',
      )}>
        {percent}%
      </span>
    </div>
  );
}
